import React, { useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { classes, useSlug } from '@bloomberg/media-ui-core';
import { ChevronUp } from '@bloomberg/media-ui-icons';

import { getLiveUrl } from '../i18n';
import { DropdownListItem, THEMES } from '../types';
import {
  TranslatedMessage,
  useTranslateMessage,
} from '../utils/translate-messages';

import {
  MenuMobileProps,
  OneLevelDeepEmptyItemProps,
  OneLevelDeepItemProps,
  OneLevelDeepProps,
  TwoLevelsDeepEmptyItemProps,
  TwoLevelsDeepItemProps,
  TwoLevelsDeepProps,
} from './types';

import styles from './MenuMobile.module.css';

const Chevron = ({ isOpen }: { isOpen: boolean }) => (
  <div className={isOpen ? styles.chevronDown : styles.chevronRight}>
    <ChevronUp />
  </div>
);

const Accordion = ({
  children,
  isOpen,
}: {
  children: React.ReactNode;
  isOpen: boolean;
}) => (
  <CSSTransition
    in={isOpen}
    mountOnEnter
    timeout={200}
    classNames={{
      enter: styles.itemsEnter,
      enterActive: styles.itemsEnterActive,
      exit: styles.itemsExit,
      exitActive: styles.itemsExitActive,
    }}
    unmountOnExit
  >
    <ul>{children}</ul>
  </CSSTransition>
);

const ListItems = ({
  items,
  trackingFunction,
}: {
  items: DropdownListItem[];
  trackingFunction: (eventLabel: string) => void;
}) => (
  <>
    {items.map((item: DropdownListItem) => (
      <a
        className={styles.link}
        href={item.url}
        key={item.id}
        onClick={() => trackingFunction(item.text)}
      >
        {item.text}
      </a>
    ))}
  </>
);

const SectionHeader = ({
  text,
  level,
}: {
  text: string;
  level: 'one' | 'two';
}) => {
  return (
    <a
      className={
        level === 'one'
          ? styles.oneLevelDeepSectionHeader
          : styles.twoLevelDeepSectionHeader
      }
    >
      {text}
    </a>
  );
};

const TwoLevelsDeepEmpty = ({ text }: TwoLevelsDeepEmptyItemProps) => {
  return (
    <a className={styles.itemText}>
      {text}
      <Chevron isOpen={false} />
    </a>
  );
};

const TwoLevelsDeepItems = ({
  text,
  items = [],
  trackingFunction,
}: TwoLevelsDeepItemProps) => {
  return (
    <ul>
      {text && <SectionHeader text={text} level={'two'} />}
      <Accordion isOpen={true}>
        <ListItems trackingFunction={trackingFunction} items={items} />
      </Accordion>
    </ul>
  );
};

const TwoLevelsDeep = ({
  id,
  dropdownListItems: items,
  text,
  trackingFunction,
}: TwoLevelsDeepProps) => {
  const hasItems = Boolean(items.length);

  const _trackingFunction = (dropdownListItemText: string) => {
    trackingFunction(text, dropdownListItemText);
  };

  if (!text) {
    return <ListItems trackingFunction={_trackingFunction} items={items} />;
  }

  return (
    <li
      className={classes([styles.listItem, styles.twoLevelsDeep])}
      aria-label={text}
      data-submenu-item-id={id}
      key={id}
    >
      {!hasItems && <TwoLevelsDeepEmpty text={text} />}
      {hasItems && (
        <TwoLevelsDeepItems
          trackingFunction={_trackingFunction}
          text={text}
          items={items}
        />
      )}
    </li>
  );
};

const OneLevelDeepItems = ({
  text,
  items = {},
  trackingFunction,
}: OneLevelDeepItemProps) => {
  const [showItems, setShowItems] = useState(false);

  const toggleItems = () => {
    setShowItems(!showItems);
  };

  return (
    <>
      {text && (
        <button
          className={classes([
            styles.itemText,
            styles.cursor,
            showItems && styles.selected,
          ])}
          onClick={() => toggleItems()}
        >
          {text}
          <Chevron isOpen={showItems} />
        </button>
      )}
      <Accordion isOpen={showItems}>
        {Object.values(items).map((item) => (
          <TwoLevelsDeep
            trackingFunction={trackingFunction}
            key={item.id}
            {...item}
          />
        ))}
      </Accordion>
    </>
  );
};

const OneLevelDeepEmpty = ({
  label,
  text,
  url,
  trackingFunction,
}: OneLevelDeepEmptyItemProps) => {
  const className = classes([
    styles.itemText,
    { [styles.itemTextWithLabel]: label },
  ]);

  return (
    <a
      href={url}
      className={className}
      onClick={() => trackingFunction('click', text)}
    >
      {text}
      {label && <span className={styles.label}>{label}</span>}
    </a>
  );
};

const OneLevelDeep = ({
  menuItem: { id, text, submenuItems: items = {} },
  trackingFunction,
}: OneLevelDeepProps) => {
  const filterItemsForMetadata = Object.fromEntries(
    Object.entries(items).filter(([key]) => key !== `metadata_${id}`)
  );
  const hasItems = Boolean(Object.keys(filterItemsForMetadata).length);
  const url = items && items[`metadata_${id}`]?.url;
  const label = items && items[`metadata_${id}`]?.label;

  const _trackingFunction = (
    submenuText: string,
    dropdownListItemText: string
  ) => {
    trackingFunction('click', `${text}-${submenuText}-${dropdownListItemText}`);
  };

  return (
    <li
      className={classes([styles.listItem, styles.oneLevelDeep])}
      aria-label={text}
      data-menu-item-id={id}
    >
      {!hasItems && !url && <SectionHeader text={text} level={'one'} />}
      {!hasItems && url && (
        <OneLevelDeepEmpty
          label={label}
          text={text}
          url={url}
          trackingFunction={trackingFunction}
        />
      )}
      {hasItems && (
        <OneLevelDeepItems
          trackingFunction={_trackingFunction}
          text={text}
          items={items}
        />
      )}
    </li>
  );
};

export const MenuMobile = ({
  theme = THEMES.DARK,
  menuItems,
  trackingFunction,
  locale,
}: MenuMobileProps) => {
  const componentName = useSlug('menu-mobile');
  const translateMessage = useTranslateMessage();

  if (!menuItems || !Object.keys(menuItems).length) {
    return null;
  }

  const liveUrl = getLiveUrl(locale);
  const cx = classes([
    styles.menuMobileContainer,
    {
      [styles.light]: theme === THEMES.LIGHT,
      [styles.dark]: theme === THEMES.DARK,
      [styles.blue]: theme === THEMES.BLUE,
    },
  ]);

  return (
    <ul
      className={cx}
      data-component={componentName}
      data-testid="menu-mobile"
      aria-label={translateMessage({
        id: 'media-ui-nav_arialabel_menu',
        defaultMessage: 'Menu',
      })}
    >
      <li
        aria-label="Live TV"
        data-menuid="live-tv"
        className={classes([styles.listItem, styles.oneLevelDeep])}
      >
        <a
          href={liveUrl}
          className={classes([styles.itemText, styles.liveNow])}
          onClick={() => trackingFunction('click', 'LiveTV')}
        >
          <TranslatedMessage
            id="media-ui-nav_liveTv"
            defaultMessage="Live TV"
          />
        </a>
      </li>
      {Object.entries(menuItems).map(([id, menuItem]) => (
        <OneLevelDeep
          trackingFunction={trackingFunction}
          menuItem={menuItem}
          key={id}
        />
      ))}
    </ul>
  );
};
